import dayjs, { Dayjs } from 'dayjs';
import { useCallback } from 'react';

import type {
  TDisableFutureDate,
  TDisablePastDate
} from '../date-picker-field.types';

interface IUseDisableDatesProps {
  disableFuture?: boolean | TDisableFutureDate;
  disablePast?: boolean | TDisablePastDate;
  /** Дополнительный отступ в днях от сегодня (работает только с boolean disableFuture/disablePast) */
  disableDays?: number;
}

/**
 * Хук для вычисления функции disabledDate по настройкам запрета диапазона дат.
 * Поддерживает boolean (вся будущая/прошедшая дата) и объект с границей (disableFrom/disableUntil).
 */
const useDisableDates = ({
  disableFuture,
  disablePast,
  disableDays
}: IUseDisableDatesProps) => {
  const today = dayjs();

  const disableByRange = useCallback(
    (day: Dayjs): boolean => {
      // Проверяем блокировку будущих дат
      if (disableFuture) {
        if (typeof disableFuture === 'boolean') {
          if (day.isAfter(today, 'day')) return true;
        } else if (disableFuture.disableBy && disableFuture.disableFrom) {
          const disableFromDate = dayjs(disableFuture.disableFrom);
          if (day.isAfter(disableFromDate, 'day')) return true;
        }
      }

      // Проверяем блокировку прошедших дат
      if (disablePast) {
        if (typeof disablePast === 'boolean') {
          return day.isBefore(today, 'day');
        } else if (disablePast.disableBy && disablePast.disableUntil) {
          const disableUntilDate = dayjs(disablePast.disableUntil);
          return day.isBefore(disableUntilDate, 'day');
        }
      }

      return false;
    },
    [disableFuture, disablePast, today]
  );

  const disableDate = useCallback(
    (day: Dayjs): boolean => {
      if (disableByRange(day)) return true;
      if (disableDays) {
        if (
          disableFuture === true &&
          day.isAfter(today.subtract(disableDays, 'days'), 'day')
        ) {
          return true;
        }
        if (
          disablePast === true &&
          day.isBefore(today.add(disableDays, 'days'), 'day')
        ) {
          return true;
        }
      }
      return false;
    },
    [disableByRange, disableDays, disableFuture, disablePast, today]
  );

  return { disableDate };
};

export default useDisableDates;
